import { useMemo } from 'react';
import { Link2, Globe, Tag, FileKey, Boxes, Building2 } from 'lucide-react';
import { KpiCard } from '../KpiCard';
import { DonutChart } from '../DonutChart';
import { CategoryRanking } from '../CategoryRanking';
import { SiteBars } from '../SiteBars';
import { WidgetCard, SectionTitle, Legend, EmptyMsg, useBarsIn, DashSkeleton } from '../primitives';

const CATS = [
  { key: 'enlaces', name: 'Enlaces', Icon: Link2, color: '#00b8d4', link: '/assets/links' },
  { key: 'dns', name: 'Registros DNS', Icon: Globe, color: '#3b82f6', link: '/assets/dns' },
  { key: 'licenses', name: 'Licencias', Icon: Tag, color: '#f59e0b', link: '/assets/licenses' },
  { key: 'certificates', name: 'Certificados', Icon: FileKey, color: '#22c55e', link: '/assets/certificates' },
];
const has = (stats, key, allowed) => !!stats && allowed[key] && Object.prototype.hasOwnProperty.call(stats, key);

export const NetworkDashboard = ({ loading, stats, providerData, allowed }) => {
  const barsIn = useBarsIn(loading);
  const ranking = useMemo(() => CATS.filter(item => has(stats, item.key, allowed))
    .map(item => ({ ...item, value: stats[item.key] })).sort((a, b) => b.value - a.value), [stats, allowed]);
  const total = ranking.reduce((sum, item) => sum + item.value, 0);
  const composition = ranking.filter(item => item.value > 0);

  if (loading) return <DashSkeleton />;

  return (
    <div className="space-y-6">
      {stats && ranking.length > 0 && <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
        <KpiCard testid="kpi-card-net-total" title="Registros de conectividad" value={total} subtitle="Suma de categorías disponibles" Icon={Boxes} />
        {has(stats, 'enlaces', allowed) && <KpiCard testid="kpi-card-net-enlaces" title="Enlaces" value={stats.enlaces} subtitle="Conexiones registradas" Icon={Link2} iconClass="bg-cyan-500/10 text-cyan-500" to="/assets/links" delay={80} />}
        {has(stats, 'licenses', allowed) && <KpiCard testid="kpi-card-net-licencias" title="Licencias" value={stats.licenses} subtitle="Registros activos, incluidos vencidos" Icon={Tag} iconClass="bg-amber-500/10 text-amber-500" to="/assets/licenses" delay={160} />}
        {has(stats, 'certificates', allowed) && <KpiCard testid="kpi-card-net-certificados" title="Certificados" value={stats.certificates} subtitle="Certificados registrados" Icon={FileKey} iconClass="bg-green-500/10 text-green-500" to="/assets/certificates" delay={240} />}
      </div>}
      {ranking.length > 0 && <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
        <WidgetCard testid="widget-net-composition" className="lg:col-span-5" delay={120}>
          <SectionTitle title="Distribución de registros" subtitle="Proporción por categoría disponible" />
          {composition.length ? <><DonutChart data={composition} centerValue={total} centerLabel="Registros" /><Legend items={composition} /></> : <EmptyMsg />}
        </WidgetCard>
        <WidgetCard testid="widget-net-ranking" className="lg:col-span-7" delay={180}>
          <SectionTitle title="Desglose por Categoría" subtitle="Ranking de registros por volumen" />
          <CategoryRanking items={ranking} total={total} barsIn={barsIn} />
        </WidgetCard>
      </div>}
      {allowed.enlaces && providerData !== null && <WidgetCard testid="widget-net-providers" delay={240}>
        <SectionTitle title="Enlaces por Proveedor" subtitle="Distribución de enlaces según proveedor" Icon={Building2} />
        {providerData.length ? <SiteBars data={providerData} barsIn={barsIn} testidPrefix="provider-item" /> : <EmptyMsg text="Sin enlaces registrados" />}
      </WidgetCard>}
    </div>
  );
};
